import { prepareForSlot } from '@mui/base'
import { type FC } from 'react'
import { useIntl } from 'react-intl'

import { type CrossSellProduct } from '@redteclab/api/clients/retail-media-xsell-service'

import { Link, Rating, Skeleton } from 'base-ui'

import {
  CSW_INTERACTED_KEY,
  useSetSessionStorageValuesForOptimizelyTracking,
} from '../../../experiments/hooks'
import { useGlobalConfigContext } from '../../../global-config'
import { NextLink } from '../../../next-link'
import { TosSuperscriptHowProductRatingCalculated } from '../../../tos-superscripts'
import { urlGetRelativeUrl } from '../../../url-handling'
import { crossSellAccessorGetLocalizedProductDeepLink } from '../../model/crossSellAccessorGetLocalizedProductDeepLink'
import { crossSellAccessorGetSponsoredProductLink } from '../../model/crossSellAccessorGetSponsoredProductLink'
import { useCrossSellMetadataContext } from '../CrossSellMetatadaProvider'

const LinkSlot = prepareForSlot(NextLink)

type CrossSellProductRatingProps = {
  product?: CrossSellProduct
}

export const CrossSellProductRating: FC<CrossSellProductRatingProps> = ({
  product,
}) => {
  const { language, tenant } = useGlobalConfigContext()
  const trackClick =
    useSetSessionStorageValuesForOptimizelyTracking(CSW_INTERACTED_KEY)
  const metadata = useCrossSellMetadataContext()
  const intl = useIntl()

  if (!product) {
    return (
      <div className="flex min-h-[var(--type-scale-xl-font-size)] flex-col items-center">
        <Skeleton className="w-[106px]" />
        <Skeleton className="mt-2 w-[40px]" />
      </div>
    )
  }

  const rating = product.bewertung_durchschnitt
    ? Math.round(Number(product.bewertung_durchschnitt.replace(',', '.')))
    : 0

  if (rating === 0) {
    return (
      <div className="invisible min-h-[calc(var(--type-scale-xl-font-size)+var(--type-scale-l-line-height)+var(--spacing-basic-4xs))]" />
    )
  }

  const sponsoredProductLink = crossSellAccessorGetSponsoredProductLink({
    metadata,
    product,
  })
  const href = sponsoredProductLink
    ? `${sponsoredProductLink}#user-reviews-section`
    : `${urlGetRelativeUrl(
        crossSellAccessorGetLocalizedProductDeepLink({
          language,
          product,
          tenant,
        }),
      )}#user-reviews-section`

  return (
    <span className="flex flex-col gap-0.5">
      <span className="flex">
        <Link
          aria-label={intl.formatMessage({ id: 'product.rate' }, { rating })}
          className="flex"
          data-qa-id="product-rating"
          href={href}
          onClick={trackClick}
          rel={sponsoredProductLink ? 'nofollow noopener' : undefined}
          slots={{ root: LinkSlot }}
        >
          <Rating
            className="text-xl"
            readOnly
            slotProps={{
              iconContainer: {
                className: '-mx-0.5',
              },
            }}
            value={rating}
          />
        </Link>
        <TosSuperscriptHowProductRatingCalculated className="top-1" />
      </span>
      <Link
        className="leading-l text-dark-primary-max"
        data-qa-id="product-rating"
        href={href}
        rel={sponsoredProductLink ? 'nofollow noopener' : undefined}
        slots={{ root: LinkSlot }}
      >{`(${product.bewertung_anzahl})`}</Link>
    </span>
  )
}
